// Wire protocol mirror of server/game/types.go. Keep in sync with the Go side.

export interface Unit {
  id: number;
  o: number;
  k: string;
  x: number;
  z: number;
  hp: number;
  m: number;
  tx: number;
  tz: number;
  st: string;
  tid?: number;
  tb?: boolean;
  tn?: boolean;
  ca?: string;
  cn?: number;
}

export interface Building {
  id: number;
  o: number;
  k: string;
  x: number;
  z: number;
  hp: number;
  m: number;
  p: number;
  ut?: number; // upgrade research 0..1 (0/absent = idle)
  lv?: number; // tower/barracks/wall upgrade tier
  q?: string[];
  qt?: number;
}

export interface ResNode {
  id: number;
  k: string;
  x: number;
  z: number;
  a: number;
}

export interface Lake {
  x: number;
  z: number;
  rx: number;
  rz: number;
  a1: number;
  p1: number;
  a2: number;
  p2: number;
}

export interface PlayerState {
  id: number;
  n: string;
  b?: boolean;
  w: number;
  g: number;
  f: number;
  p: number;
  c: number;
  a: boolean;
}

export interface PStat {
  id: number;
  w: number;
  g: number;
  f: number;
  tr: number;
  bd: number;
  lu: number;
  lb: number;
  k: number;
}

export interface Shot {
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  k: string; // 'archer' (arrow) or tower tiers: 'tower' → 'bullet' → 'missile' → 'bomb'
}

export interface Snapshot {
  tick: number;
  u: Unit[];
  bd: Building[];
  nd: ResNode[];
  lk?: Lake[];
  pl: PlayerState[];
  sh?: Shot[];
  st?: PStat[];
  win: number;
}

export interface Cmd {
  a: 'move' | 'stop' | 'gather' | 'attack' | 'amove' | 'build' | 'train' | 'upgrade' | 'convert';
  ids?: number[];
  x?: number;
  z?: number;
  tid?: number;
  tb?: boolean;
  kind?: string;
  bid?: number;
}

export interface RoomInfo {
  id: string;
  players: string[];
  seats: number;
  started: boolean;
  difficulty?: string;
  richness?: string;
}

export type ServerMsg =
  | { t: 'rooms'; rooms: RoomInfo[] }
  | { t: 'joined'; room: string; pid: number }
  | { t: 'state'; snap: Snapshot }
  | { t: 'event'; event: string; room?: string; detail?: string }
  | { t: 'error'; msg: string }
  | { t: 'lobby'; room: string; pid: number; detail?: string; players?: string[]; seats?: number; started?: boolean; difficulty?: string; richness?: string; bots?: string[] };

export const DIFFICULTY_INFO: Record<string, { name: string; icon: string; blurb: string }> = {
  easy: { name: 'Easy', icon: '🌱', blurb: 'A little dumb: slow, small armies, swords only' },
  medium: { name: 'Medium', icon: '🧑', blurb: 'Plays like a solid human' },
  hard: { name: 'Hard', icon: '🔥', blurb: 'Relentless: fast macro, early two-prong attacks' },
};

export const RICHNESS_INFO: Record<string, { name: string; icon: string; blurb: string }> = {
  less: { name: 'Less', icon: '🦗', blurb: 'Lean map — every tree counts' },
  normal: { name: 'Normal', icon: '🌾', blurb: 'Classic balance' },
  large: { name: 'Large', icon: '🌳', blurb: 'Rich groves and deep mines' },
  abundant: { name: 'Abundant', icon: '💰', blurb: 'Near-endless bounty' },
};

export const TEAM_COLORS = [
  '#3b82f6', // blue
  '#ef4444', // red
  '#22c55e', // green
  '#eab308', // yellow
  '#a855f7', // purple
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

export const UNIT_INFO: Record<string, { name: string; icon: string }> = {
  villager: { name: 'Villager', icon: '🧑‍🌾' },
  swordsman: { name: 'Swordsman', icon: '⚔️' },
  archer: { name: 'Archer', icon: '🏹' },
  spearman: { name: 'Spearman', icon: '🔱' },
  rajput: { name: 'Rajput', icon: '🛡️' },
};

export const BLD_INFO: Record<
  string,
  { name: string; icon: string; cost: string; hotkey: string; range?: number }
> = {
  townCenter: { name: 'Town Center', icon: '🏛️', cost: '', hotkey: '' },
  house: { name: 'House (+5 pop)', icon: '🏠', cost: '30🪵', hotkey: 'H' },
  farm: { name: 'Farm (endless 🍎)', icon: '🌾', cost: '60🪵', hotkey: 'G' },
  barracks: { name: 'Barracks', icon: '⚒️', cost: '150🪵', hotkey: 'R' },
  tower: { name: 'Watchtower', icon: '🗼', cost: '100🪵 50💰', hotkey: 'T', range: 10 },
  wall: { name: 'Palisade (wood wall)', icon: '🪵', cost: '10🪵', hotkey: 'Y' },
  gate: { name: 'Gate (auto-door)', icon: '🛡️', cost: '30🪵', hotkey: 'G' },
};

// Watchtower upgrade tiers (mirrors server TowerTiers). Index = level.
export interface TowerTier {
  name: string;
  icon: string;
  weapon: string;
  dmg: number;
  range: number;
  cost: string;
  w: number;
  g: number;
  hp: number; // max-HP added on reaching the tier
}

export const TOWER_LEVELS: TowerTier[] = [
  { name: 'Lookout', icon: '🏹', weapon: 'bolts', dmg: 10, range: 10, cost: '', w: 0, g: 0, hp: 0 },
  { name: 'Gunner', icon: '🔫', weapon: 'bullets', dmg: 16, range: 11, cost: '150🪵 100💰', w: 150, g: 100, hp: 150 },
  { name: 'Rocketeer', icon: '🚀', weapon: 'missiles', dmg: 26, range: 12, cost: '200🪵 175💰', w: 200, g: 175, hp: 150 },
  { name: 'Siege', icon: '💣', weapon: 'bombs', dmg: 40, range: 13, cost: '300🪵 250💰', w: 300, g: 250, hp: 200 },
];

export const MAX_TOWER_LVL = TOWER_LEVELS.length - 1;

// Barracks upgrade tiers (mirrors server BarracksTiers). Index = level.
export interface BarracksTier {
  name: string;
  icon: string;
  unlocks: string; // unit kind unlocked ('' for base)
  unlockName: string;
  cost: string;
  w: number;
  g: number;
  hp: number; // max-HP added on reaching the tier
}

export const BARRACKS_LEVELS: BarracksTier[] = [
  { name: 'Militia', icon: '⚒️', unlocks: '', unlockName: '', cost: '', w: 0, g: 0, hp: 0 },
  { name: 'Armory', icon: '🔱', unlocks: 'spearman', unlockName: 'Spearman', cost: '175🪵 75💰', w: 175, g: 75, hp: 150 },
  { name: 'Royal Akhara', icon: '🛡️', unlocks: 'rajput', unlockName: 'Rajput', cost: '250🪵 175💰', w: 250, g: 175, hp: 200 },
];

export const MAX_BARRACKS_LVL = BARRACKS_LEVELS.length - 1;

// Wall upgrade tiers (mirrors server WallTiers). Index = level.
// 0 = wooden palisade (the build), 1 = heavy stone wall.
export interface WallTier {
  name: string;
  icon: string;
  cost: string;
  w: number;
  g: number;
  hp: number; // max-HP added on reaching the tier
}

export const WALL_LEVELS: WallTier[] = [
  { name: 'Palisade', icon: '🪵', cost: '', w: 0, g: 0, hp: 0 },
  { name: 'Stone Wall', icon: '🧱', cost: '50🪵 20💰', w: 50, g: 20, hp: 450 },
];

export const MAX_WALL_LVL = WALL_LEVELS.length - 1;

// Gate tier names: gates ride the wall tiers mechanically (same costs, HP,
// research), but a wooden gate becomes a stone *gate* — never a wall.
export const GATE_LEVEL_NAMES = ['Wooden Gate', 'Stone Gate'];

// Structured training costs (mirrors server UnitStats).
export const TRAIN_COSTS: Record<string, { w: number; g: number; f: number }> = {
  villager: { w: 0, g: 0, f: 50 },
  swordsman: { w: 0, g: 25, f: 60 },
  archer: { w: 45, g: 40, f: 25 },
  spearman: { w: 20, g: 0, f: 50 },
  rajput: { w: 0, g: 60, f: 90 },
};

// Structured build costs (mirrors server BldStats) for ghost validity.
export const BLD_COSTS: Record<string, { w: number; g: number; f: number }> = {
  townCenter: { w: 0, g: 0, f: 0 },
  house: { w: 30, g: 0, f: 0 },
  farm: { w: 60, g: 0, f: 0 },
  barracks: { w: 150, g: 0, f: 0 },
  tower: { w: 100, g: 50, f: 0 },
  wall: { w: 10, g: 0, f: 0 },
};
